import _styled, { default as _styled2 } from "styled-components";
import NextHead from "next/head";
import React from "react";
import { mergeRefs } from "react-merge-refs";
import type MuxPlayerElement from "@mux/mux-player";
import type MuxPlayerComponent from "@mux/mux-player-react";
import { type Ratio } from "~/features/ui/aspect-ratio";
import { useIubendaPreferences } from "~/features/app/scripts/iubenda";
import { optimizeMuxImageUrl, useMuxPlayerA11yPatch, type MuxCSSProperties } from "./utils";
import { usePageLoaded } from "./use-page-loaded";
import { useInView } from "./use-in-view";
import { isHlsVideoSource, useHlsVideo } from "./use-hls-video";

// `next/dynamic` wraps the loaded component in a `LoadableComponent` that
// uses `useImperativeHandle` to expose `{ retry }` on the ref, which means
// refs never reach the underlying `<mux-player>` element. `React.lazy`
// preserves ref forwarding through to the loaded `forwardRef` component, so
// we use it directly and gate it behind a client-only flag to mimic the old
// `ssr: false` behavior.
const MuxPlayerLazy = React.lazy(() => import("@mux/mux-player-react"));
const MuxPlayer = React.forwardRef<MuxPlayerElement, React.ComponentProps<typeof MuxPlayerComponent>>(function MuxPlayer(props, ref) {
  return <React.Suspense fallback={null}>
        <MuxPlayerLazy {...props} ref={ref} />
      </React.Suspense>;
}) as typeof MuxPlayerComponent;
export type Props = {
  src?: string;
  ratio?: Ratio;
  rounded?: boolean;
  autoPlay?: boolean;
  loop?: boolean;
  fit?: "cover" | "fill";
  controls?: React.ReactNode;
  videoRef?: React.Ref<HTMLVideoElement | null>;
  poster?: string;
  posterMaxWidth?: number;
  priority?: boolean;
  height?: number;
  mimeType?: string;
};
function withMuxMinResolution(url?: string, minResolution: string = "720p") {
  if (!url || !url.includes("mux.com")) {
    return url;
  }
  const [base, query = ""] = url.split("?");
  const params = new URLSearchParams(query);
  if (!params.has("min_resolution")) {
    params.set("min_resolution", minResolution);
  }
  const queryString = params.toString();
  return queryString ? `${base}?${queryString}` : base;
}
const videoFit = {
  fill: {
    "height": "100%",
    "width": "100%",
    "maxWidth": "100%"
  },
  cover: {
    "position": "absolute",
    "inset": "0",
    "height": "100%",
    "width": "100%"
  }
} as const;
export function InlineVideo(props: Props) {
  const {
    src,
    ratio = "16/9",
    rounded = true,
    autoPlay = true,
    loop = true,
    fit = "fill",
    controls,
    poster,
    posterMaxWidth = 1920,
    priority = false,
    videoRef = null,
    height,
    mimeType
  } = props;
  const muxPoster = poster && poster.includes("image.mux.com") ? optimizeMuxImageUrl(poster, posterMaxWidth) : poster;
  const pageLoaded = usePageLoaded();
  const earlyInViewOptions = React.useMemo(() => ({
    rootMargin: "500px 0px"
  }), []);
  const [earlyInViewRef, earlyInView] = useInView(earlyInViewOptions);
  const [inViewRef, inView] = useInView();
  const containerRef = React.useMemo(() => mergeRefs([earlyInViewRef, inViewRef] as React.Ref<HTMLDivElement>[]),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  const shouldMount = pageLoaded && earlyInView;
  const shouldPlay = pageLoaded && inView;
  // `priority` (above-the-fold hero media) skips the window-load / in-view
  // gating: the native <video> is server-rendered, so its poster paints with
  // the initial HTML and the video downloads immediately instead of trailing
  // every other resource on the page.
  const shouldLoad = priority || pageLoaded && inView;
  const playerRef = React.useRef<HTMLVideoElement | MuxPlayerElement | null>(null);
  const [playerEl, setPlayerEl] = React.useState<HTMLVideoElement | MuxPlayerElement | null>(null);
  const setPlayerRef = React.useCallback((el: HTMLVideoElement | MuxPlayerElement | null) => {
    playerRef.current = el;
    setPlayerEl(el);
  }, []);
  const preferences = useIubendaPreferences();
  const mergedRef = React.useMemo(() => mergeRefs([setPlayerRef, videoRef]), [setPlayerRef, videoRef]);
  const htmlVideoRef = React.useRef<HTMLVideoElement | null>(null);
  const setHtmlVideoRef = React.useCallback((element: HTMLVideoElement | null) => {
    htmlVideoRef.current = element;
    setPlayerRef(element);
  }, [setPlayerRef]);
  const mergedHtmlVideoRef = React.useMemo(() => mergeRefs([setHtmlVideoRef, videoRef]), [setHtmlVideoRef, videoRef]);
  const hlsSource = React.useMemo(() => ({
    mimeType,
    src
  }), [mimeType, src]);
  const isHls = isHlsVideoSource(hlsSource);
  useHlsVideo({
    autoPlay,
    enabled: shouldLoad,
    source: hlsSource,
    videoRef: htmlVideoRef
  });
  useMuxPlayerA11yPatch(playerEl);

  // React 19 + @mux/mux-player-react 3.1.0 drops boolean attributes that
  // mux-player-react serializes as empty strings (`muted=""`, `autoplay=""`,
  // `loop=""`, `nohotkeys=""`). Set them imperatively on the element instead
  // so background videos auto-play muted on loop as intended.
  React.useEffect(() => {
    const el = playerEl;
    if (!el || typeof (el as Element).setAttribute !== "function") return;
    if (autoPlay) el.setAttribute("autoplay", "");else el.removeAttribute("autoplay");
    if (loop) el.setAttribute("loop", "");else el.removeAttribute("loop");
    el.setAttribute("muted", "");
    el.setAttribute("nohotkeys", "");
    (el as HTMLVideoElement).muted = true;
  }, [playerEl, autoPlay, loop]);
  return <>
      {priority && muxPoster && <NextHead>
          <link key={`preload-poster-${muxPoster}`} rel="preload" as="image" href={muxPoster} />
        </NextHead>}
      <_StyledDiv ref={containerRef as React.Ref<HTMLDivElement>} style={{
      aspectRatio: ratio,
      height
    }} $_css={[{
      "position": "relative",
      "overflow": "hidden"
    }, rounded && {
      "borderRadius": "4px"
    }, videoFit[fit]]}>
        {controls}
        {src?.includes("mux.com") ? shouldMount && <MuxPlayer muted ref={mergedRef as React.Ref<MuxPlayerElement>} title="Background video" aria-hidden nohotkeys autoPlay={shouldPlay ? autoPlay : false} loop={loop} playsInline streamType="on-demand" disableCookies={!preferences.measurement} disableTracking={!preferences.measurement} src={withMuxMinResolution(src)} primaryColor="#FFFFFF" secondaryColor="#000000" defaultHiddenCaptions defaultShowRemainingTime={false} disablePictureInPicture preload="metadata" maxResolution={typeof window !== "undefined" && window.innerWidth < 768 ? "720p" : "1440p"} poster={muxPoster} onError={(event: Event) => {
        // Background videos: suppress errors so they don't bubble up
        // to the page and to keep the console clean. The poster image
        // is shown as fallback if playback fails.
        event.stopPropagation();
        event.stopImmediatePropagation?.();
      }} style={{
        "--media-object-fit": "cover",
        "--controls": "none",
        "--media-error-dialog": "none",
        height: "100%",
        width: "100%"
      } as MuxCSSProperties} /> : shouldLoad ? <_StyledVideo poster={muxPoster} ref={mergedHtmlVideoRef} src={isHls ? undefined : src} autoPlay={autoPlay} loop={loop} muted playsInline onContextMenu={e => e.preventDefault()} $_css2={[{
        "pointerEvents": "none",
        "position": "absolute",
        "inset": "0",
        "height": "100%",
        "width": "100%",
        "objectFit": "cover"
      }, {
        "pointerEvents": "none",
        "position": "absolute",
        "inset": "0",
        "height": "100%",
        "width": "100%",
        "objectFit": "cover"
      }]} /> : null}
      </_StyledDiv>
    </>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-1fp7vy3-0"
})`${p => p.$_css}`;
var _StyledVideo = /*#__PURE__*/_styled2("video").withConfig({
  displayName: "inline-video___StyledVideo",
  componentId: "sc-13vjcd2-0"
})(["", ""], p => p.$_css2);